import { memo, useMemo } from 'react';
import { VStack, HStack } from '@cbhq/cds-web/layout';
import { TextTitle3, TextBody, TextHeadline } from '@cbhq/cds-web/typography';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@cbhq/cds-web/tables';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { ListCell } from '@cbhq/cds-web/cells/ListCell';
import { DisplayType, format } from '@/utils/numberFormatter';
import { PoolTransaction, PoolDetailsData } from './hooks/usePoolDetailsData';

type Props = {
  pool: PoolDetailsData;
  transactions: PoolTransaction[];
};

const columns = [
  { label: 'Transaction', field: 'type' },
  { label: 'Total Value', field: 'totalValue' },
  { label: 'Token 0', field: 'baseAmount' },
  { label: 'Token 1', field: 'quoteAmount' },
  { label: 'Time', field: 'timestamp' },
];

function formatTransactionType(type: PoolTransaction['type']): string {
  switch (type) {
    case 'swap':
      return 'Swap';
    case 'add':
      return 'Add';
    case 'remove':
      return 'Remove';
    default:
      return type;
  }
}

function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

/**
 * Desktop row component for transactions table
 */
const TransactionDesktopRow = memo(function TransactionDesktopRow({
  tx,
  pool,
}: {
  tx: PoolTransaction;
  pool: PoolDetailsData;
}) {
  return (
    <TableRow>
      <TableCell alignItems="flex-start">
        <TextBody as="span">{formatTransactionType(tx.type)}</TextBody>
      </TableCell>
      <TableCell alignItems="flex-start">
        <TextBody as="span">
          {format({ value: tx.totalValue, type: DisplayType.FiatShort })}
        </TextBody>
      </TableCell>
      <TableCell alignItems="flex-start">
        <TextBody as="span">
          {format({ value: tx.baseAmount, type: DisplayType.TokenAmount })} {pool.baseSymbol}
        </TextBody>
      </TableCell>
      <TableCell alignItems="flex-start">
        <TextBody as="span">
          {format({ value: tx.quoteAmount, type: DisplayType.TokenAmount })} {pool.quoteSymbol}
        </TextBody>
      </TableCell>
      <TableCell alignItems="flex-start">
        <TextBody as="span" color="fgMuted">
          {formatTimeAgo(tx.timestamp)}
        </TextBody>
      </TableCell>
    </TableRow>
  );
});

/**
 * Mobile row component for transactions
 */
const TransactionMobileRow = memo(function TransactionMobileRow({
  tx,
  pool,
}: {
  tx: PoolTransaction;
  pool: PoolDetailsData;
}) {
  return (
    <ListCell
      title={formatTransactionType(tx.type)}
      description={formatTimeAgo(tx.timestamp)}
      detail={format({ value: tx.totalValue, type: DisplayType.FiatShort })}
      subdetail={`${format({ value: tx.baseAmount, type: DisplayType.TokenAmount })} ${
        pool.baseSymbol
      }`}
    />
  );
});

/**
 * PoolDetailsTransactions - displays transaction history for the pool
 */
export const PoolDetailsTransactions = memo(function PoolDetailsTransactions({
  pool,
  transactions,
}: Props) {
  const { isPhone, isPhoneLandscape, isTablet } = useBreakpoints();
  const isMobile = isPhone || isPhoneLandscape || isTablet;

  const sortedTransactions = useMemo(() => {
    return [...transactions].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }, [transactions]);

  if (isMobile) {
    return (
      <VStack background="bg" borderRadius={400} overflow="hidden">
        {/* Header */}
        <VStack paddingX={2} paddingY={2}>
          <TextTitle3 as="h3">Transactions</TextTitle3>
        </VStack>

        {/* Mobile list */}
        {sortedTransactions.map((tx) => (
          <TransactionMobileRow key={tx.id} tx={tx} pool={pool} />
        ))}
      </VStack>
    );
  }

  return (
    <VStack background="bg" borderRadius={400} overflow="hidden">
      {/* Header */}
      <VStack paddingX={2} paddingY={2}>
        <TextTitle3 as="h3">Transactions</TextTitle3>
      </VStack>

      {/* Desktop table */}
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map(({ label }) => (
              <TableCell key={label} alignItems="flex-start">
                <HStack>
                  <TextHeadline as="span" color="fgMuted">
                    {label}
                  </TextHeadline>
                </HStack>
              </TableCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedTransactions.map((tx) => (
            <TransactionDesktopRow key={tx.id} tx={tx} pool={pool} />
          ))}
        </TableBody>
      </Table>
    </VStack>
  );
});
